import React, { useEffect, useState } from "react";
import { useUpdateUser, useGetUserById } from "../../../../api/adduser"; // Adjust import path as needed
import { useParams } from "react-router";
import { useNavigate } from "react-router-dom";
import { notifySuccess, notifyError } from "../../../../utils/ToastifyProvider";

const EditUser = () => {
  const { id: userId } = useParams();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    gender: "",
    password: "",
    confirmPassword: "",
    companyName: "",
  });
  const navigate = useNavigate();

  const {
    data: userData,
    isLoading: isFetchingUser,
    error: fetchError,
    refetch,
  } = useGetUserById(userId);

  const { mutate, isLoading: isUpdating } = useUpdateUser();

  // Pre-fill form once data is loaded
  useEffect(() => {
    if (userData) {
      setFormData({
        firstName: userData?.user?.firstName || "",
        lastName: userData?.user?.lastName || "",
        phone: userData?.user?.mobile || "",
        email: userData?.user?.email || "",
        gender: userData?.user?.gender || "",
        companyName: userData?.user?.companyName || "",
        password: "",
        confirmPassword: "",
      });
    }
  }, [userData]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (formData.password && formData.password !== formData.confirmPassword) {
      notifyError("Passwords do not match.");
      return;
    }

    const updatedData = { ...formData };
    if (!formData.password) {
      delete updatedData.password;
      delete updatedData.confirmPassword;
    }

    mutate(
      { userId, data: updatedData },
      {
        onSuccess: () => {
          notifySuccess("User updated successfully!");
          navigate("/user"); // redirect after success
          refetch();
        },
        onError: (err) => {
          notifyError(err.response?.data?.message || "Update failed.");
        },
      }
    );
  };

  if (!userId) return <div className="text-center py-10">User ID missing!</div>;
  if (isFetchingUser)
    return <div className="text-center py-10">Loading user data...</div>;
  if (fetchError)
    return (
      <div className="text-center py-10 text-red-500">
        Failed to load user data.
      </div>
    );

  return (
    <div className="min-h-screen flex items-center justify-center bg-white px-4 py-10">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl bg-gray-50 border border-gray-300 rounded-lg shadow p-8"
      >
        <h2 className="text-3xl font-bold text-center mb-6 text-black">
          Edit User
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* First Name */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.firstName}
              onChange={(e) => handleChange("firstName", e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md py-2 px-4"
              placeholder="Enter First Name"
            />
          </div>

          {/* Last Name */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={formData.lastName}
              onChange={(e) => handleChange("lastName", e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md py-2 px-4"
              placeholder="Enter Last Name"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Phone <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange("phone", e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md py-2 px-4"
              placeholder="Enter Phone"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange("email", e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md py-2 px-4"
              placeholder="Enter Email"
            />
          </div>

          {/* Gender */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Gender <span className="text-red-500">*</span>
            </label>
            <select
              value={formData.gender}
              onChange={(e) => handleChange("gender", e.target.value)}
              required
              className="w-full border border-gray-300 rounded-md py-2 px-4 bg-white"
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Company Name */}
          <div>
            <label className="block text-sm font-semibold mb-1">
              Company Name
            </label>
            <input
              type="text"
              value={formData.companyName}
              onChange={(e) => handleChange("companyName", e.target.value)}
              className="w-full border border-gray-300 rounded-md py-2 px-4"
              placeholder="Enter Company Name"
            />
          </div>
        </div>

        <div className="flex justify-end gap-4 mt-8">
          <button
            type="submit"
            disabled={isUpdating}
            className={`bg-primaryLight text-primary hover:text-white border border-primary rounded-full px-6 py-2 font-medium hover:bg-primary transition ${
              isUpdating ? "opacity-50 cursor-not-allowed" : ""
            }`}
          >
            {isUpdating ? "Updating..." : "Update User"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditUser;
